<div class="content-info">
    <div class="content-title d-flex justify-content-between align-items-center">
        <h3 class="title">My Account</h3>
    </div>

    @if (isLoading) {
    <p class="text-muted">Loading your account…</p>
    } @else {

    <!-- Read-only identity: username is the login id, roles are admin-managed -->
    <div class="row g-3 mb-4">
        <div class="col-md-3 col-sm-6">
            <label class="form-label">Username</label>
            <input type="text" class="form-control" [value]="username" readonly disabled />
            <small class="text-muted">Your login name cannot be changed.</small>
        </div>
        <div class="col-md-3 col-sm-6">
            <label class="form-label">Role</label>
            <input type="text" class="form-control" [value]="roleNames" readonly disabled />
            <small class="text-muted">Managed by an administrator.</small>
        </div>
    </div>

    <!-- ── My details ───────────────────────────────────────────────────── -->
    <form [formGroup]="profileForm" (ngSubmit)="saveProfile()">
        <h5 class="mb-3">My Details</h5>
        <div class="row g-3 mb-3">
            <div class="col-md-3 col-sm-6">
                <label class="form-label">Name</label>
                <input type="text" formControlName="name" class="form-control" placeholder="e.g., John Doe" />
                @if (profileForm.get('name')?.invalid && profileForm.get('name')?.touched) {
                <small class="text-danger">Name is required</small>
                }
            </div>

            <div class="col-md-3 col-sm-6">
                <label class="form-label">Email</label>
                <input type="email" formControlName="email" class="form-control"
                    placeholder="e.g., john@example.com" />
                @if (profileForm.get('email')?.invalid && profileForm.get('email')?.touched) {
                <small class="text-danger">Enter a valid email</small>
                }
            </div>

            <div class="col-md-3 col-sm-6">
                <label class="form-label">Mobile</label>
                <input type="text" formControlName="mobile" class="form-control" placeholder="e.g., 9876543210" />
                @if (profileForm.get('mobile')?.invalid && profileForm.get('mobile')?.touched) {
                <small class="text-danger">Mobile must be 10 digits</small>
                }
            </div>

            <div class="col-md-3 col-sm-6 d-flex align-items-end">
                <button type="submit" class="btn btnprimary" [disabled]="profileForm.invalid || isSavingProfile">
                    {{ isSavingProfile ? 'Saving…' : 'Save Details' }}
                </button>
            </div>
        </div>
    </form>

    <hr class="my-4" />

    <!-- ── Change password ──────────────────────────────────────────────── -->
    <form [formGroup]="passwordForm" (ngSubmit)="savePassword()">
        <h5 class="mb-3">Change Password</h5>
        <div class="row g-3">
            <div class="col-md-3 col-sm-6">
                <label class="form-label">Current Password</label>
                <div class="input-group">
                    <input [type]="show.current ? 'text' : 'password'" formControlName="currentPassword"
                        class="form-control" autocomplete="current-password" />
                    <button class="btn btn-outline-secondary" type="button" (click)="toggle('current')"
                        [attr.aria-label]="show.current ? 'Hide current password' : 'Show current password'">
                        <i class="bi" [ngClass]="show.current ? 'bi-eye-slash' : 'bi-eye'"></i>
                    </button>
                </div>
                @if (passwordForm.get('currentPassword')?.invalid && passwordForm.get('currentPassword')?.touched) {
                <small class="text-danger">Current password is required</small>
                }
            </div>

            <div class="col-md-3 col-sm-6">
                <label class="form-label">New Password</label>
                <div class="input-group">
                    <input [type]="show.next ? 'text' : 'password'" formControlName="newPassword" class="form-control"
                        autocomplete="new-password" />
                    <button class="btn btn-outline-secondary" type="button" (click)="toggle('next')"
                        [attr.aria-label]="show.next ? 'Hide new password' : 'Show new password'">
                        <i class="bi" [ngClass]="show.next ? 'bi-eye-slash' : 'bi-eye'"></i>
                    </button>
                </div>
                @if (passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched) {
                <small class="text-danger">At least 6 characters</small>
                }
            </div>

            <div class="col-md-3 col-sm-6">
                <label class="form-label">Confirm New Password</label>
                <div class="input-group">
                    <input [type]="show.confirm ? 'text' : 'password'" formControlName="confirmPassword"
                        class="form-control" autocomplete="new-password" />
                    <button class="btn btn-outline-secondary" type="button" (click)="toggle('confirm')"
                        [attr.aria-label]="show.confirm ? 'Hide confirmation' : 'Show confirmation'">
                        <i class="bi" [ngClass]="show.confirm ? 'bi-eye-slash' : 'bi-eye'"></i>
                    </button>
                </div>
                @if (passwordForm.errors?.['passwordMismatch'] && passwordForm.get('confirmPassword')?.touched) {
                <small class="text-danger">Passwords do not match</small>
                }
            </div>

            <div class="col-md-3 col-sm-6">
                <label class="form-label">New PIN <span class="text-muted">(optional)</span></label>
                <div class="input-group">
                    <input [type]="show.pin ? 'text' : 'password'" formControlName="pin" class="form-control"
                        inputmode="numeric" maxlength="4" placeholder="e.g., 1234" />
                    <button class="btn btn-outline-secondary" type="button" (click)="toggle('pin')"
                        [attr.aria-label]="show.pin ? 'Hide PIN' : 'Show PIN'">
                        <i class="bi" [ngClass]="show.pin ? 'bi-eye-slash' : 'bi-eye'"></i>
                    </button>
                </div>
                @if (passwordForm.get('pin')?.invalid && passwordForm.get('pin')?.touched) {
                <small class="text-danger">PIN must be 4 digits</small>
                }
            </div>
        </div>

        <div class="mt-3">
            <button type="submit" class="btn btnprimary" [disabled]="passwordForm.invalid || isSavingPassword">
                {{ isSavingPassword ? 'Updating…' : 'Update Password' }}
            </button>
            <small class="text-muted ms-3">
                Your current password is required to change your password or PIN.
            </small>
        </div>
    </form>
    }
</div>
